import React, { useEffect, useImperativeHandle, forwardRef } from "react";
import { renderTapCard } from "@tap-payments/card-sdk";
import { useTranslation } from "react-i18next";
import LoadingIndicator from "../LoadingIndicator";
import {
  MadaPaymentCard,
  MasterPaymentCard,
  VisaPaymentCard,
} from "../icons/Icons";

const TapPayment = forwardRef(
  (
    {
      hideHeader,
      onTokenizationSuccess,
      onTokenizationError,
      onReady,
      onCardValid,
      customer,
      transaction,
      interfaceConfig,
      fields: customFields,
      addons: customAddons,
      acceptance: customAcceptance,
      children,
    },
    ref,
  ) => {
    const { t } = useTranslation("signup");
    const containerRef = React.useRef(null);
    const [showLoader, setShowLoader] = React.useState(true);
    const pendingTokenizeRef = React.useRef(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
      let isMounted = true;
      let sdkInstance = null;

      if (containerRef.current) {
        // Clear container and create a fresh node for the SDK
        containerRef.current.innerHTML = "";
        const sdkChild = document.createElement("div");
        sdkChild.id = "tap-card-sdk-inner";
        containerRef.current.appendChild(sdkChild);

        try {
          sdkInstance = renderTapCard("tap-card-sdk-inner", {
            publicKey: process.env.REACT_APP_TAP_PUBLIC_KEY,
            merchant: {
              id: process.env.REACT_APP_TAP_MERCHANT_ID,
            },
            transaction: {
              amount: transaction?.amount,
              currency: transaction?.currency || "KWD",
            },
            customer: customer || {},
            fields: {
              cardHolder: false,
              ...customFields,
            },
            addons: {
              displayPaymentBrands: false,
              loader: true,
              saveCard: false,
              ...customAddons,
            },
            acceptance: {
              supportedBrands: [
                "AMERICAN_EXPRESS",
                "MASTERCARD",
                "VISA",
                "MADA",
              ],
              supportedCards: ["CREDIT", "DEBIT"],
              ...customAcceptance,
            },
            interface: {
              locale: "ar",
              theme: "dark",
              direction: "rtl",
              ...interfaceConfig,
            },
            onSuccess: (data) => {
              if (isMounted) {
                if (pendingTokenizeRef.current) {
                  pendingTokenizeRef.current.resolve(data.id);
                  pendingTokenizeRef.current = null;
                }
                if (onTokenizationSuccess) onTokenizationSuccess(data.id);
              }
            },
            onError: (error) => {
              if (isMounted) {
                if (pendingTokenizeRef.current) {
                  pendingTokenizeRef.current.reject(error);
                  pendingTokenizeRef.current = null;
                }
                if (onTokenizationError) onTokenizationError(error);
              }
            },
            onReady: () => {
              console.log("Tap Card SDK Ready");
              setShowLoader(false);
              if (onReady) onReady(true);
            },
            onValidInput: (data) => {
              if (isMounted && onCardValid) onCardValid(data);
            },
          });
        } catch (err) {
          console.error("Tap Card SDK Initialization Error:", err);
        }
      }

      return () => {
        isMounted = false;
        if (sdkInstance && typeof sdkInstance.unmount === "function") {
          // Use setTimeout to avoid unmounting during React render
          setTimeout(() => {
            sdkInstance.unmount();
          }, 0);
        }
        if (containerRef.current) {
          containerRef.current.innerHTML = "";
        }
      };
    }, [transaction?.amount, transaction?.currency]);

    useImperativeHandle(ref, () => ({
      tokenize: () => {
        const CardSDK = window.CardSDK;
        if (!CardSDK || typeof CardSDK.tokenize !== "function") {
          return Promise.reject(new Error("CardSDK.tokenize is not available"));
        }

        return new Promise((resolve, reject) => {
          pendingTokenizeRef.current = { resolve, reject };
          CardSDK.tokenize();
        });
      },
    }));

    return (
      <>

        {
          !hideHeader && (
            <div className="payment-card">
              <div className="head-payment-card">
                <div className="content">
                  <h4>{t("pay_by_credit_card", "ادفع بواسطة بطاقة الدفع")}</h4>
                  <p>من فضلك ادخل بيانات بطاقة الدفع الخاصة بك</p>
                </div>
                <div className="card-types">
                  <MadaPaymentCard />
                  <VisaPaymentCard />
                  <MasterPaymentCard />
                </div>
              </div>
            </div>
          )
        }
        
        <div className={`payment-form card-payment-bg ${hideHeader ? 'add-card' : ''}`}>
          {showLoader && (
            <div className="grid-full d-flex justify-content-center align-items-center loading">
              <LoadingIndicator {...hideHeader ? {color: 'var(--color-primary)'} : {}}/>
            </div>
          )}
          <div
            ref={containerRef}
            className="grid-full"
            style={{
              paddingBottom: "10px",
              display: showLoader ? "none" : "block",
            }}
          ></div>
          {children}
        </div>
      </>
    );
  },
);

export default TapPayment;
